/**
 * VariationAnalysis is the main component of the page used to add, edit and delete variation analyses.
 * It is encapsulated in a PyramidCard.
 * This component contains the context and the subcomponents used in this page: Datagrid and AddEditPopup.
 * This component retrieves data from the API and transmits it to the subcomponents.
 */

import React, { useState, useEffect } from "react";
import Datagrid from "./Datagrid";
import AddEditPopup from "./AddEditPopup";
import { osirisApi } from "../../../../../services/api/osiris";
import { useAuth } from "../../../../../providers/AuthProvider";
import PyramidCard from "../../../../../components/common/pyramid/PyramidCard";
import notify from "devextreme/ui/notify";

/**
 * Main component
 */
const VariationAnalysis = () => {
  const osiris = osirisApi();
  const { accessToken, companyList, userList } = useAuth();

  /* State variables */
  const [isLoading, setIsLoading] = useState(true);
  const [variationAnalyses, setVariationAnalyses] = useState([]);
  const [datasets, setDatasets] = useState([]);
  const [editingVariationAnalysis, setEditingVariationAnalysis] =
    useState(null);

  /**
   * Converts the data received from the backend to the format expected by the OptionPopup component
   * @param {Object} backendData - Data received from the backend
   * @returns {Object} - Formatted data for the component
   */
  const convertBackendDataToFormData = (backendData) => {
    if (!backendData) return {};

    const formattedData = {
      ...backendData,
    };

    if (backendData.bucketRulesDeparture) {
      formattedData.DepartureBucket = true;
      formattedData.DeparturebucketRules = backendData.bucketRulesDeparture;
    }

    if (backendData.actuarialRateRulesDeparture) {
      formattedData.DepartureActuarialRate = true;
      formattedData.DepartureactuarialRateRules =
        backendData.actuarialRateRulesDeparture;
    }

    if (backendData.bucketRulesArrival) {
      formattedData.ArrivalBucket = true;
      formattedData.ArrivalbucketRules = backendData.bucketRulesArrival;
    }

    if (backendData.actuarialRateRulesArrival) {
      formattedData.ArrivalActuarialRate = true;
      formattedData.ArrivalactuarialRateRules =
        backendData.actuarialRateRulesArrival;
    }

    return formattedData;
  };

  /**
   * Retrieves data from the API.
   */
  const fetchData = async () => {
    if (!accessToken) {
      return;
    }

    setIsLoading(true);
    try {
      const [datasetsData, variationAnalysesData] = await Promise.all([
        osiris.getDatasets({
          onlyReady: true,
          includeUserTransitiveGroups: true,
        }),
        osiris.getVariations({
          onlyReady: false,
          includeUserTransitiveGroups: true,
        }),
      ]);
      const variationAnalyses = Array.isArray(variationAnalysesData)
        ? variationAnalysesData
        : variationAnalysesData.result || [];
      const datasets = Array.isArray(datasetsData)
        ? datasetsData
        : datasetsData.result || [];
      setVariationAnalyses(variationAnalyses);
      setDatasets(datasets);
    } catch (error) {
      console.error("Erreur lors de la récupération des données:", error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Retrieves data from the API when the component is mounted or when the accessToken changes.
   */
  useEffect(() => {
    fetchData();
  }, [accessToken]);

  /**
   * Retrieves data from the API when the refreshVariationAnalyses event is triggered.
   */
  useEffect(() => {
    const handleRefresh = () => {
      fetchData();
    };

    window.addEventListener("refreshVariationAnalyses", handleRefresh);
    return () =>
      window.removeEventListener("refreshVariationAnalyses", handleRefresh);
  }, [accessToken]);

  // Uses the editingVariationAnalysis state to determine if the add/edit popup is visible and if we are in edit mode
  const isAddEditPopupVisible = Boolean(editingVariationAnalysis !== null);
  const editMode = Boolean(editingVariationAnalysis?.id);

  /**
   * Handles the click event on a row in the datagrid.
   *
   * @param {object} rowData - The data of the clicked row
   */
  const handleRowClick = (rowData) => {
    const formattedData = convertBackendDataToFormData(rowData);
    setEditingVariationAnalysis(formattedData);
  };

  /**
   * Opens the add popup in add mode.
   */
  const handleAddButtonClick = () => {
    setEditingVariationAnalysis({});
  };

  /**
   * Handles the click on the cancel button and closes the add/edit popup by setting the editingVariationAnalysis state to null.
   */
  const onCancel = () => {
    setEditingVariationAnalysis(null);
  };

  /**
   * Confirms the add/edit action and updates the data source.
   *
   * @param {object} data - The data to save
   */
  const onConfirm = async (data) => {
    try {
      if (editMode) {
        const updatedData = {
          id: editingVariationAnalysis.id,
          description: data.description,
        };
        await osiris.updateVariationAnalysis(updatedData);
        fetchData();
        setEditingVariationAnalysis(null);
      } else {
        const newData = {
          description: data.description,
          couponMethod: data.couponMethod,
          interpolationType: data.interpolationType,
          datasetDepartureId: data.datasetDepartureId,
          datasetArrivalId: data.datasetArrivalId,
        };

        if (data.DepartureBucket) {
          newData.bucketRulesDeparture = data.DeparturebucketRules;
        }

        if (data.DepartureActuarialRate) {
          newData.actuarialRateRulesDeparture =
            data.DepartureactuarialRateRules;
        }

        if (data.ArrivalBucket) {
          newData.bucketRulesArrival = data.ArrivalbucketRules;
        }

        if (data.ArrivalActuarialRate) {
          newData.actuarialRateRulesArrival = data.ArrivalactuarialRateRules;
        }

        await osiris.addVariationAnalysis(newData);
        fetchData();
        setEditingVariationAnalysis(null);
      }
    } catch (error) {
      console.error("Error:", error.message);
      notify(error.message || "An error occurred", "error", 3000);
    }
  };

  return (
    <div>
      <Datagrid
        companyList={companyList}
        userList={userList}
        variationList={(variationAnalyses || []).map(
          convertBackendDataToFormData
        )}
        datasetList={datasets || []}
        onAddButtonClick={handleAddButtonClick}
        onRowClick={handleRowClick}
        isLoading={isLoading}
      />
      <AddEditPopup
        isVisible={isAddEditPopupVisible}
        onCancel={onCancel}
        onConfirm={onConfirm}
        inputFormData={editingVariationAnalysis || {}}
        datasetList={datasets || []}
        editMode={editMode}
      />
    </div>
  );
};

/**
 * Page component using PyramidCard
 */
const VariationAnalysisAdd = () => {
  return (
    <PyramidCard title="Add Variation Analysis">
      <VariationAnalysis />
    </PyramidCard>
  );
};

export default VariationAnalysisAdd;
